import api from "@/lib/api";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

/** Convertit la clé publique VAPID base64url en Uint8Array (format attendu par PushManager) */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf;
}

export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export function currentPermission() {
  if (!("Notification" in window)) return "denied";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/** Enregistre le SW (idempotent) */
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  return reg;
}

/** Demande la permission ET enregistre une subscription sur le backend.
 *  Retourne {ok: true, subscription} ou {ok: false, reason}.
 */
export async function enablePushNotifications() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  if (!VAPID_PUBLIC_KEY) return { ok: false, reason: "no_vapid_key" };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: "denied" };

  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: "sw_failed" };

  // Vérifie si déjà abonné
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const subJson = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
    user_agent: navigator.userAgent.slice(0, 200),
  });

  return { ok: true, subscription: sub };
}

/** Désinscrit + supprime côté backend. */
export async function disablePushNotifications() {
  if (!("serviceWorker" in navigator)) return { ok: true };
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe();
  }
  return { ok: true };
}

/** Renvoie {permission, subscribed} courant. */
export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, permission: "denied", subscribed: false };
  const permission = currentPermission();
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      subscribed = !!sub;
    }
  } catch {}
  return { supported: true, permission, subscribed };
}
